import React from "react";
import type { BiomarkerSuggestionItem } from "./types";

type ChatSelectionContextValue = {
  selectedMetricPromptByMessageId: Record<string, string>;
  selectedBiomarkerPromptByMessageId: Record<string, string>;
  scrollToBottomRequestId: number;
  setSelectedMetricPrompt: (messageId: string, prompt: string) => void;
  setSelectedBiomarkerPrompt: (messageId: string, prompt: string) => void;
  selectBiomarkerSuggestion: (
    messageId: string,
    suggestion: BiomarkerSuggestionItem
  ) => void;
  requestScrollToBottom: () => void;
  resetSelections: () => void;
};

const ChatSelectionContext =
  React.createContext<ChatSelectionContextValue | null>(null);

type ProviderProps = {
  children: React.ReactNode;
  selectedMetricPromptByMessageId: Record<string, string>;
  selectedBiomarkerPromptByMessageId: Record<string, string>;
  scrollToBottomRequestId: number;
  setSelectedMetricPrompt: (messageId: string, prompt: string) => void;
  setSelectedBiomarkerPrompt: (messageId: string, prompt: string) => void;
  selectBiomarkerSuggestion: (
    messageId: string,
    suggestion: BiomarkerSuggestionItem
  ) => void;
  requestScrollToBottom: () => void;
  resetSelections: () => void;
};

export const ChatSelectionProvider = ({
  children,
  selectedMetricPromptByMessageId,
  selectedBiomarkerPromptByMessageId,
  scrollToBottomRequestId,
  setSelectedMetricPrompt,
  setSelectedBiomarkerPrompt,
  selectBiomarkerSuggestion,
  requestScrollToBottom,
  resetSelections,
}: ProviderProps) => {
  const value = React.useMemo(
    () => ({
      selectedMetricPromptByMessageId,
      selectedBiomarkerPromptByMessageId,
      scrollToBottomRequestId,
      setSelectedMetricPrompt,
      setSelectedBiomarkerPrompt,
      selectBiomarkerSuggestion,
      requestScrollToBottom,
      resetSelections,
    }),
    [
      resetSelections,
      requestScrollToBottom,
      scrollToBottomRequestId,
      selectedBiomarkerPromptByMessageId,
      selectedMetricPromptByMessageId,
      setSelectedBiomarkerPrompt,
      setSelectedMetricPrompt,
      selectBiomarkerSuggestion,
    ]
  );

  return (
    <ChatSelectionContext.Provider value={value}>
      {children}
    </ChatSelectionContext.Provider>
  );
};

export const useChatSelections = () => {
  const context = React.useContext(ChatSelectionContext);

  if (!context) {
    throw new Error("useChatSelections must be used within ChatSelectionProvider");
  }

  return context;
};
